
import { Link } from 'react-router-dom';

export default function SelfDeterminationTheoryPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-sky-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 via-cyan-600 to-sky-600 text-white py-20">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto">
            <Link to="/motivation" className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors">
              <i className="ri-arrow-left-line mr-2"></i>
              動機付け一覧に戻る
            </Link>
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl mb-6">
              <i className="ri-user-star-line text-4xl"></i>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">自己決定理論</h1>
            <p className="text-xl text-white/90">Self-Determination Theory：SDT</p>
            <p className="text-lg text-white/80 mt-2">提唱者：Deci & Ryan</p>
          </div>
        </div>
      </section>

      {/* Core Message */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl shadow-2xl p-8 md:p-12 text-white text-center">
              <h2 className="text-3xl font-bold mb-4">自律性・有能感・関係性の3要素が満たされると内発的動機づけが高まる</h2>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto space-y-12">
            {/* 3つの基本的心理欲求 */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-blue-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-focus-3-line text-xl text-white"></i>
                </div>
                3つの基本的心理欲求
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-200">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-compass-3-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">自律性</h3>
                  <p className="text-sm text-gray-600">Autonomy</p>
                  <p className="text-gray-700 text-sm leading-relaxed mt-3">
                    自分で選択し、自分の意志で行動しているという感覚
                  </p>
                </div>
                <div className="bg-gradient-to-br from-cyan-50 to-sky-50 rounded-xl p-6 border border-cyan-200">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-medal-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">有能感</h3>
                  <p className="text-sm text-gray-600">Competence</p>
                  <p className="text-gray-700 text-sm leading-relaxed mt-3">
                    自分には能力があり、成果を出せるという感覚
                  </p>
                </div>
                <div className="bg-gradient-to-br from-sky-50 to-blue-50 rounded-xl p-6 border border-sky-200">
                  <div className="w-12 h-12 bg-gradient-to-br from-sky-500 to-sky-600 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-team-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">関係性</h3>
                  <p className="text-sm text-gray-600">Relatedness</p>
                  <p className="text-gray-700 text-sm leading-relaxed mt-3">
                    他者とつながり、所属しているという感覚
                  </p>
                </div>
              </div>
            </div>

            {/* 薬局運営への活用 */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-blue-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-hospital-line text-xl text-white"></i>
                </div>
                薬局運営への活用
              </h2>
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                    <i className="ri-search-eye-line text-blue-600 mr-2"></i>
                    やらされ感の正体分析
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    職員が「やらされ感」を感じている場合、3つの欲求のどれが満たされていないかを分析します。
                  </p>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-blue-500 mr-2 mt-0.5"></i>
                      <span><strong>自律性の欠如：</strong>細かく指示されすぎ、自分で判断する余地がない</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-blue-500 mr-2 mt-0.5"></i>
                      <span><strong>有能感の欠如：</strong>成長実感がない、フィードバックがない</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-blue-500 mr-2 mt-0.5"></i>
                      <span><strong>関係性の欠如：</strong>孤立している、チームの一員と感じられない</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-cyan-50 to-sky-50 rounded-xl p-6 border border-cyan-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                    <i className="ri-settings-4-line text-cyan-600 mr-2"></i>
                    指示・KPI・評価設計の軸
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    3つの欲求を軸に、マネジメント施策を設計します。
                  </p>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-white rounded-lg p-4 border border-blue-100">
                      <h4 className="font-bold text-gray-900 mb-2 text-sm">自律性を高める</h4>
                      <ul className="text-xs text-gray-600 space-y-1">
                        <li>・業務の進め方を任せる</li>
                        <li>・「なぜ」を説明する</li>
                        <li>・選択肢を与える</li>
                      </ul>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-cyan-100">
                      <h4 className="font-bold text-gray-900 mb-2 text-sm">有能感を高める</h4>
                      <ul className="text-xs text-gray-600 space-y-1">
                        <li>・適切な難易度の目標</li>
                        <li>・具体的なフィードバック</li>
                        <li>・成長の可視化</li>
                      </ul>
                    </div>
                    <div className="bg-white rounded-lg p-4 border border-sky-100">
                      <h4 className="font-bold text-gray-900 mb-2 text-sm">関係性を高める</h4>
                      <ul className="text-xs text-gray-600 space-y-1">
                        <li>・チーム目標の共有</li>
                        <li>・相互支援の仕組み</li>
                        <li>・感謝を伝える文化</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-sky-50 to-blue-50 rounded-xl p-6 border border-sky-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                    <i className="ri-user-settings-line text-sky-600 mr-2"></i>
                    マネジャーの役割再定義
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    マネジャーの役割は「指示・管理」ではなく、<strong className="text-blue-600">3つの欲求を満たす環境を整えること</strong>です。職員が自律的に動き、成長を実感し、チームの一員として貢献できる環境をつくることが、マネジャーの最も重要な仕事です。
                  </p>
                </div>
              </div>
            </div>

            {/* 実践チェックリスト */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-blue-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-checkbox-multiple-line text-xl text-white"></i>
                </div>
                実践チェックリスト
              </h2>
              <div className="space-y-4">
                <div className="flex items-start p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <i className="ri-checkbox-blank-circle-line text-blue-500 mr-3 mt-1"></i>
                  <span className="text-gray-700">職員に業務の「やり方」を任せる余地があるか？</span>
                </div>
                <div className="flex items-start p-4 bg-cyan-50 rounded-lg border border-cyan-200">
                  <i className="ri-checkbox-blank-circle-line text-cyan-500 mr-3 mt-1"></i>
                  <span className="text-gray-700">指示を出すとき「なぜそれが必要か」を説明しているか？</span>
                </div>
                <div className="flex items-start p-4 bg-sky-50 rounded-lg border border-sky-200">
                  <i className="ri-checkbox-blank-circle-line text-sky-500 mr-3 mt-1"></i>
                  <span className="text-gray-700">職員が成長を実感できるフィードバックを定期的に行っているか？</span>
                </div>
                <div className="flex items-start p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <i className="ri-checkbox-blank-circle-line text-blue-500 mr-3 mt-1"></i>
                  <span className="text-gray-700">チームの目標や成果を全員で共有しているか？</span>
                </div>
                <div className="flex items-start p-4 bg-cyan-50 rounded-lg border border-cyan-200">
                  <i className="ri-checkbox-blank-circle-line text-cyan-500 mr-3 mt-1"></i>
                  <span className="text-gray-700">職員同士が助け合える仕組みがあるか？</span>
                </div>
              </div>
            </div>

            {/* 明日の一手 */}
            <div className="bg-gradient-to-br from-blue-600 via-cyan-600 to-sky-600 rounded-2xl shadow-2xl p-8 text-white">
              <h2 className="text-2xl font-bold mb-4 flex items-center">
                <i className="ri-calendar-check-line text-3xl mr-3"></i>
                明日の一手
              </h2>
              <p className="text-lg text-white/90 leading-relaxed mb-6">
                職員一人ひとりの「自律性・有能感・関係性」の充足度を観察してみましょう。
              </p>
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-6">
                <p className="text-white font-semibold mb-2">具体的なアクション：</p>
                <p className="text-white/90 leading-relaxed">
                  次回の1on1で「最近、自分で判断できる場面はありますか？」「成長を感じられていますか？」「チームの一員として貢献できていると感じますか？」と問いかけてみる。
                </p>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center pt-8 border-t border-gray-200">
              <Link to="/motivation/motivation-theory" className="flex items-center text-blue-600 hover:text-blue-700 font-semibold transition-colors">
                <i className="ri-arrow-left-line mr-2"></i>
                動機づけ理論
              </Link>
              <Link to="/motivation/intrinsic-extrinsic" className="flex items-center text-blue-600 hover:text-blue-700 font-semibold transition-colors">
                内発的／外発的動機づけ
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
